/* eslint-disable unicorn/filename-case -- kebab-case per docs/conventions/core.md:
   this file exports pure functions, not a React component. */
export type DimeTreeComboboxNode = {
  id: string;
  label: string;
  children?: readonly DimeTreeComboboxNode[];
};

export type DimeTreeComboboxRow = {
  node: DimeTreeComboboxNode;
  /** 0 for roots. */
  depth: number;
  parentId: string | null;
  canExpand: boolean;
  isExpanded: boolean;
  /** Shown only because a descendant matches the query. */
  isAncestorOnly: boolean;
  /** 1-based among visible siblings. */
  posInSet: number;
  setSize: number;
};

function childrenOf(node: DimeTreeComboboxNode): readonly DimeTreeComboboxNode[] {
  return node.children ?? [];
}

function matchesQuery(node: DimeTreeComboboxNode, normalizedQuery: string): boolean {
  return node.label.toLowerCase().includes(normalizedQuery);
}

/** True when the node or any descendant matches, so the node must stay visible. */
function subtreeMatches(node: DimeTreeComboboxNode, normalizedQuery: string): boolean {
  return (
    matchesQuery(node, normalizedQuery) ||
    childrenOf(node).some((child) => subtreeMatches(child, normalizedQuery))
  );
}

function collectRows(options: {
  siblings: readonly DimeTreeComboboxNode[];
  depth: number;
  parentId: string | null;
  expandedIds: ReadonlySet<string>;
  normalizedQuery: string;
  rows: DimeTreeComboboxRow[];
}): void {
  const { siblings, depth, parentId, expandedIds, normalizedQuery, rows } = options;
  const isSearching = normalizedQuery !== '';
  const visible = isSearching
    ? siblings.filter((node) => subtreeMatches(node, normalizedQuery))
    : siblings;

  visible.forEach((node, index) => {
    const canExpand = childrenOf(node).length > 0;
    const hasVisibleChildren = isSearching
      ? childrenOf(node).some((child) => subtreeMatches(child, normalizedQuery))
      : canExpand && expandedIds.has(node.id);
    rows.push({
      node,
      depth,
      parentId,
      canExpand,
      isExpanded: hasVisibleChildren,
      isAncestorOnly: isSearching && !matchesQuery(node, normalizedQuery),
      posInSet: index + 1,
      setSize: visible.length,
    });
    if (hasVisibleChildren) {
      collectRows({
        siblings: childrenOf(node),
        depth: depth + 1,
        parentId: node.id,
        expandedIds,
        normalizedQuery,
        rows,
      });
    }
  });
}

/**
 * Flattened, depth-first rows to render. While searching, matches plus their ancestors are
 * shown fully expanded and `expandedIds` is ignored; otherwise only children of expanded nodes
 * appear.
 */
export function visibleTreeRows(options: {
  roots: readonly DimeTreeComboboxNode[];
  expandedIds: ReadonlySet<string>;
  query: string;
}): DimeTreeComboboxRow[] {
  const rows: DimeTreeComboboxRow[] = [];
  collectRows({
    siblings: options.roots,
    depth: 0,
    parentId: null,
    expandedIds: options.expandedIds,
    normalizedQuery: options.query.trim().toLowerCase(),
    rows,
  });
  return rows;
}

function findNode(
  nodes: readonly DimeTreeComboboxNode[],
  id: string,
): DimeTreeComboboxNode | undefined {
  for (const node of nodes) {
    if (node.id === id) return node;
    const found = findNode(childrenOf(node), id);
    if (found) return found;
  }
  return undefined;
}

function descendantIds(node: DimeTreeComboboxNode): Set<string> {
  const ids = new Set<string>();
  const stack = [...childrenOf(node)];
  for (let next = stack.pop(); next; next = stack.pop()) {
    ids.add(next.id);
    stack.push(...childrenOf(next));
  }
  return ids;
}

/**
 * Toggles one node in the minimal set of explicitly checked ids. Checking a node drops its
 * descendants (now implied); a node already included by a checked ancestor, or one that is not
 * in the tree, leaves the value unchanged.
 */
export function toggleTreeSelection(options: {
  roots: readonly DimeTreeComboboxNode[];
  value: readonly string[];
  id: string;
}): string[] {
  const { roots, value, id } = options;
  if (value.includes(id)) return value.filter((checkedId) => checkedId !== id);

  const node = findNode(roots, id);
  if (!node) return [...value];
  if (includedByAncestor({ roots, value }).has(id)) return [...value];

  const implied = descendantIds(node);
  return [...value.filter((checkedId) => !implied.has(checkedId)), id];
}

/** Maps each descendant of a checked node to the label of its outermost checked ancestor. */
export function includedByAncestor(options: {
  roots: readonly DimeTreeComboboxNode[];
  value: readonly string[];
}): ReadonlyMap<string, string> {
  const checked = new Set(options.value);
  const included = new Map<string, string>();

  const walk = (node: DimeTreeComboboxNode, checkedAncestorLabel: string | null): void => {
    if (checkedAncestorLabel !== null) included.set(node.id, checkedAncestorLabel);
    const labelForChildren = checkedAncestorLabel ?? (checked.has(node.id) ? node.label : null);
    for (const child of childrenOf(node)) walk(child, labelForChildren);
  };
  for (const root of options.roots) walk(root, null);

  return included;
}

/** Ids of every ancestor of the given ids, used to expand the ancestors of checked nodes. */
export function ancestorIdsOf(options: {
  roots: readonly DimeTreeComboboxNode[];
  ids: readonly string[];
}): Set<string> {
  const targets = new Set(options.ids);
  const ancestors = new Set<string>();

  // Returns whether the node or anything beneath it is a target.
  const walk = (node: DimeTreeComboboxNode): boolean => {
    let hasTargetBelow = false;
    for (const child of childrenOf(node)) {
      if (walk(child)) hasTargetBelow = true;
    }
    if (hasTargetBelow) ancestors.add(node.id);
    return hasTargetBelow || targets.has(node.id);
  };
  for (const root of options.roots) walk(root);

  return ancestors;
}
